import { PureComponent } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { push as changeLocation } from 'connected-react-router';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import { Intent, Button, Position } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';

import { AppToaster } from '@f/components/notification-toaster/AppToaster';
import { findReports, createReport, removeReports } from '@f/services/report/report.actions';
import { isDesigningModeEnabled } from '@f/services/report/report.selectors';
import { withDataActions, withSelectability } from '@f/components/higher-order';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import Table from '@f/components/shared/table/Table';
import { PdfDownloadButton } from '@f/components/pages/dashboard/reports/components/PdfDownloadButton';
import { pluralize } from '@f/utils/pluralize';

import cls from './ReportListPage.module.css';

const REMOVE_REPORT_TOAST_KEY = 'remove_report_toast';

class ReportListPage extends PureComponent {
  static propTypes = {
    match: PropTypes.object,
    items: PropTypes.array,
    pagination: PropTypes.object,
    selectionMap: PropTypes.object,
    selectionMode: PropTypes.bool,
    isDesigningMode: PropTypes.bool,
    designingId: PropTypes.string,
    isProcessing: PropTypes.bool,
    isFindingReports: PropTypes.bool,
    changeLocation: PropTypes.func,
    findNextItems: PropTypes.func,
    createReport: PropTypes.func,
    removeItems: PropTypes.func,
    toggleItemSelection: PropTypes.func,
    toggleAllItemsSelection: PropTypes.func,
    deselectAllItems: PropTypes.func,
  };

  schema = [
    {
      label: 'TITLE',
      grow: true,
      renderer: item => (
        <NavLink
          className={cls.reportTitle}
          to={`/dashboard/reports/${item.id}`}
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {item.title || 'Untitled'}
        </NavLink>
      ),
      cellProps: item => ({
        className: cx(cls.primary, { [cls.italic]: !item.title }),
      }),
    },
    {
      label: 'NUMBER OF ITEMS',
      renderer: item => item.itemCount,
    },
    {
      label: 'CREATED AT',
      renderer: item => new Date(item.createdAt).toLocaleString(),
    },
    {
      label: '',
      renderer: item => {
        return (
          <>
            <Tooltip2 content="Download" placement={Position.BOTTOM}>
              <PdfDownloadButton reportId={item.id} />
            </Tooltip2>
            <Tooltip2 content="Remove" placement={Position.BOTTOM}>
              <Button minimal icon="trash" onClick={this.remove(item)} />
            </Tooltip2>
          </>
        );
      },
    },
  ];

  componentDidMount() {
    this.props.findNextItems({
      $sort: {
        createdAt: -1,
      },
    });
  }

  isRowSelected = item => {
    const { selectionMap } = this.props;
    return Boolean(selectionMap[item.id]);
  };

  buildRowProps = item => {
    const { designingId } = this.props;

    return {
      'className': cx({
        [cls.rowEdited]: item.id === designingId,
      }),
      'data-test': `item-row-${item.id}`,
    };
  };

  navigateToItem = item => {
    this.props.changeLocation(`/dashboard/reports/${item.id}`);
  };

  nagivateToEditedReport = () => {
    const { designingId } = this.props;
    this.props.changeLocation(`/dashboard/reports/${designingId}`);
  };

  create = async () => {
    const { match } = this.props;

    const id = await this.props.createReport();
    this.props.changeLocation(`${match.path}/${id}`);
  };

  removeReports = async ids => {
    try {
      await this.props.removeItems(ids);
    } catch (error) {
      AppToaster.show(
        {
          intent: Intent.DANGER,
          icon: 'trash',
          message: 'Error removing reports',
        },
        REMOVE_REPORT_TOAST_KEY
      );
      return;
    }
    AppToaster.show(
      {
        intent: Intent.SUCCESS,
        icon: 'trash',
        message: `${pluralize(ids.length, 'Report has', `${ids.length} reports have`)} been removed`,
      },
      REMOVE_REPORT_TOAST_KEY
    );
  };

  removeSelected = () => {
    const { selectionMap } = this.props;
    const selectedIds = Object.keys(selectionMap).filter(id => selectionMap[id]);

    this.removeReports(selectedIds);

    this.props.deselectAllItems();
  };

  remove = item => e => {
    e.stopPropagation();
    e.preventDefault();

    this.removeReports([item.id]);
  };

  render() {
    const { items, pagination, selectionMode, isDesigningMode, isProcessing, isFindingReports } = this.props;
    const { total, start, size } = pagination;

    return (
      <div className={cls.page} data-test="report-list-page">
        <Toolbar>
          <Toolbar.Title>Reports</Toolbar.Title>

          {isDesigningMode && (
            <Toolbar.Item alignRight>
              <Button
                large
                intent={Intent.PRIMARY}
                text="Show edited report"
                icon="document"
                onClick={this.nagivateToEditedReport}
              />
            </Toolbar.Item>
          )}

          <Toolbar.Item alignRight>
            <Button
              large
              disabled={Boolean(isDesigningMode)}
              intent={Intent.PRIMARY}
              text="New report"
              icon="add"
              onClick={this.create}
            />
          </Toolbar.Item>
        </Toolbar>

        <div className={cls.listActions}>
          <Button
            large
            minimal
            disabled={!selectionMode || isProcessing}
            text="Remove"
            icon="trash"
            onClick={this.removeSelected}
          />
          <p className={cls.listActionsLight}>
            Showing {items.length} of {total}
          </p>
        </div>

        <Table
          className={cls.list}
          schema={this.schema}
          items={items}
          interactive
          selectable
          isRowSelected={this.isRowSelected}
          loading={isFindingReports}
          loadingCaption="Loading reports..."
          rowProps={this.buildRowProps}
          emptyStateProps={{
            title: 'No reports',
            description: "You don't have any reports yet.",
            action: <Button large intent={Intent.PRIMARY} text="Start a new report" icon="add" onClick={this.create} />,
          }}
          onRowClick={this.navigateToItem}
          onRowSelectionChange={this.props.toggleItemSelection}
          onAllRowsSelectionChange={this.props.toggleAllItemsSelection}
        >
          {start + size < total && (
            <Table.After className={cls.footer}>
              <Button
                large
                minimal
                disabled={isFindingReports}
                loading={isFindingReports}
                text="Show more reports"
                icon="double-chevron-down"
                rightIcon="double-chevron-down"
                onClick={this.props.findNextItems}
              />
            </Table.After>
          )}
        </Table>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const reportState = state.report;
  const data = reportState.data;
  const isDesigningMode = isDesigningModeEnabled(state);
  const designingId = reportState.designingId;
  const isFindingReports = reportState.find.processing > 0;
  const findingReportsError = reportState.find.error;
  const isCreatingReport = reportState.create.processing > 0;
  const creatingReportError = reportState.create.error;
  const isRemovingReports = reportState.remove.processing > 0;
  const removingReportError = reportState.remove.error;

  const isProcessing = Boolean(isFindingReports || isCreatingReport || isRemovingReports);

  return {
    data,
    isDesigningMode,
    designingId,
    isProcessing,
    isFindingReports,
    findingReportsError,
    isCreatingReport,
    creatingReportError,
    isRemovingReports,
    removingReportError,
  };
}

export default _.flowRight([
  withRouter,
  connect(mapStateToProps, {
    changeLocation,
    findReports,
    createReport,
    removeReports,
  }),
  withDataActions(props => ({
    find: props.findReports,
    remove: props.removeReports,
  })),
  withSelectability(),
])(ReportListPage);
